"use client"
import React, { useState } from "react"
import { Box, Typography, Chip, SxProps, Theme, Container, Grid, alpha } from "@mui/material"
import SlideUpAnimation from "../utility/SlideUpAnimation"
import { ToggleButton } from "../buttons/ToggleButton"
import { getTheme } from "@/theme"
import { Plan, PricingCard } from "./PricingCard"
import { ContactUsButton } from "./buttons/ContactUsButton"
import { UpgradeButton } from "./buttons/UpgradeButton"
import { SignUpButton } from "./buttons/SignUpButton"
import { InlineLink } from "../links/InlineLink"
import { DOC_RAB_URL } from "@/constants/routes"

const plans: Plan[] = [
    {
        id: "lite",
        name: "Recall lite",
        icon: "svgs/lite-plan.svg",
        monthly: "0",
        yearly: "0",
        subheading: "Your go-to read-it-later app",
        featuresSubHeading: "Includes",
        features: [
            { content: "10 free content summaries & chats: YouTube, PDFs, articles and more" },
            { content: "Unlimited read-it-later storage" },
            { content: "Unlimited personal notes" },
        ],
        buttonComponent: SignUpButton,
    },
    {
        id: "plus",
        name: "Recall plus",
        icon: "svgs/plus-plan.svg",
        monthly: "10",
        yearly: "7",
        subheading: "Unlock your second brain",
        featuresSubHeading: "Everything in lite, plus",
        features: [
            { content: "Unlimited content summaries & chats: YouTube, PDFs, articles and more" },
            {
                content: (
                    <>
                        <InlineLink target="_blank" href={DOC_RAB_URL}>
                            Augmented Browsing
                        </InlineLink>{" "}
                        - resurfaces related content from your knowledge base as you browse.
                    </>
                ),
                highlight: true,
            },
            { content: "Automatic categorization" },
            { content: "Automatic knowledge graph" },
            { content: "Unlimited AI quiz questions" },
            { content: "Multi-language support" },
        ],

        tag: "Popular",
        buttonComponent: UpgradeButton,
    },
    {
        id: "business",
        name: "Recall business",
        icon: "svgs/business-plan.svg",
        subheading: "For collective company intelligence",
        featuresSubHeading: "Contact us for details",
        features: [],
        buttonComponent: ContactUsButton,
    },
]

export const PricingSection = () => {
    const [isYearly, setIsYearly] = useState(true)

    const priceKey = isYearly ? "yearly" : "monthly"

    return (
        <Container component="section" sx={{ p: "0 !important", mb: 25 }}>
            <SlideUpAnimation delay="0.6s">
                <Box sx={styles.toggleContainer}>
                    <Typography component="label" htmlFor="pricing-toggle">
                        Monthly
                    </Typography>
                    <ToggleButton
                        id="pricing-toggle"
                        aria-label="Toggle between monthly and yearly pricing"
                        checked={isYearly}
                        onChange={() => setIsYearly((prev) => !prev)}
                    />
                    <Typography component="label" htmlFor="pricing-toggle">
                        Yearly
                    </Typography>
                    <Chip
                        component="span"
                        label="Save 35%"
                        size="small"
                        disabled={!isYearly}
                        sx={styles.savingsChip}
                    />
                </Box>
            </SlideUpAnimation>
            <SlideUpAnimation delay="0.8s">
                <Grid container spacing={4}>
                    {plans.map((plan) => (
                        <Grid item xs={12} sm={6} lg={4} key={plan.id}>
                            <PricingCard plan={plan} priceKey={priceKey} />
                        </Grid>
                    ))}
                </Grid>
            </SlideUpAnimation>
        </Container>
    )
}

const styles: Record<string, SxProps<Theme>> = {
    spinnerContainer: {
        width: "100%",
        height: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },
    toggleContainer: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 2,
        mb: 6.25,
    },
    savingsChip: {
        p: (theme) => theme.spacing(2, 1),
        fontWeight: "500",
        fontSize: "1rem",
        color: (theme) => theme.palette.info.light,
        backgroundColor: (theme) => alpha(theme.palette.info.light, 0.2),
        border: (theme) => `1px solid ${alpha(theme.palette.info.light, 0.3)}`,
    },
}
